<template>
    <div>
        <grid
            ref="charges"
            :is-auto-height="true"
            :is-auto-width="true"
            :is-search="false"
            :getRowClass="getRowClass"
            :get-row-id="getRowId"
            :row-data="value"
            :column-defs="columnDefs"
            :style="{ marginBottom: '1rem' }"
            :pinnedBottomRowData="totals"
            :getRowStyle="getRowStyle"
            grid-theme="ag-theme-alpine-compact"
            :enableCellTextSelection="false"
            :headerHeight="50"
        ></grid>
        <modal id="delete-payment-modal" title="Delete Transaction" @ok="deletePayment">
            <p>Are you sure you want to delete this transaction?</p>
        </modal>
    </div>
</template>

<script>
    import format from 'date-fns/format';
    import parse from 'date-fns/parse';
    import isEmpty from 'lodash/isEmpty';
    import isNil from 'lodash/isNil';
    import { mapGetters } from 'vuex';
    import { formatResponseError } from '../../../../utils/api';
    import { escapeHTML } from '../../../../utils/strings';
    import API from '../../../../api';
    import FormInput from '../../../generic/form-input/form-input.vue';
    import Grid from '../../../generic/grid/grid.vue';
    import Modal from '../../../generic/modal/modal.vue';
    import { invoiceMixin } from '../../mixins/invoice-mixin';
    import ActionsCellRendererRemoveItem from './actions-cell-renderer-delete-item.vue';
    import TransactionPaymentMethodCellRenderer from '../../../generic/grid/transaction-payment-method-cell-renderer.vue';
    import { RESERVATION_INVOICE_DELETE_BILL_ENDPOINT } from '../../../../endpoints';
    import {
        TRANSACTION_VARIANT_AUTOMATED_PAYMENT_NOTIFICATION,
        TRANSACTION_VARIANT_AUTOMATED_PAYMENT_PREDICTION,
    } from '../../../../constants';

    export default {
        name: 'TransactionHistoryTable',
        components: {
            Grid,
            Modal,
            actionsCellRendererRemoveItem: ActionsCellRendererRemoveItem,
            transactionPaymentMethodCellRenderer: TransactionPaymentMethodCellRenderer,
        },
        mixins: [invoiceMixin],
        props: {
            value: Array,
            totals: Array,
            bookingMethod: String,
            metaReservationId: Number,
        },
        computed: {
            ...mapGetters('header', ['userCurrencySymbol']),
            ...mapGetters('header', ['userUnicodeDateFormat']),
        },
        data() {
            return {
                isEdit: false,
                paymentId: null,
                columnDefs: [
                    {
                        headerName: '',
                        maxWidth: 50,
                        flex: 1,
                        sortable: false,
                        resizable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRendererRemoveItem',
                        cellRendererParams: {
                            deleteItem: (id, reservationId) => {
                                this.paymentId = id;
                                this.$bvModal.show('delete-payment-modal');
                            },
                        },
                    },
                    {
                        headerName: 'Date',
                        sortable: false,
                        resizable: false,
                        field: 'date',
                        flex: 1,
                    },
                    {
                        headerName: 'Description',
                        sortable: false,
                        resizable: false,
                        field: 'description',
                        minWidth: 300,
                        flex: 8,
                        cellRenderer: 'transactionPaymentMethodCellRenderer',
                        cellRendererParams: {
                            refresh: async (id) => {
                                await this.refreshReservationInvoice(this.metaReservationId);
                            },
                        },
                        autoWidthExclude: true,
                    },
                    {
                        headerName: 'Debits(+) Credits(-)',
                        sortable: false,
                        resizable: false,
                        field: 'amount',
                        cellClass: 'ag-right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                    },
                    {
                        headerName: 'Charges Balance',
                        sortable: false,
                        resizable: false,
                        field: 'charges_balance',
                        cellRenderer: ({ data }) => data && data.status === 0 ? '' : data.charges_balance,
                        cellClass: 'ag-right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                    },
                    {
                        headerName: 'Deposits Balance',
                        sortable: false,
                        resizable: false,
                        field: 'other_deposits_balance',
                        cellRenderer: ({ data }) => data && data.status === 0 ? '' : data.other_deposits_balance,
                        cellClass: 'ag-right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                    },
                ],
            };
        },
        methods: {
            getRowClass({ data }) {
                if (data.variant === TRANSACTION_VARIANT_AUTOMATED_PAYMENT_PREDICTION) {
                    return 'automated-payment-prediction';
                } else if (data.variant === TRANSACTION_VARIANT_AUTOMATED_PAYMENT_NOTIFICATION) {
                    return 'automated-payment-notification';
                }
                return '';
            },
            getRowId(item) {
                return item.data.position?.toString();
            },
            getRowStyle(params) {
                if (params.node.rowPinned === 'bottom') {
                    return {
                        'font-weight': 'bold',
                        'background-color': '#F8F8F8',
                    };
                }
            },
            async deletePayment() {
                this.busy = true;
                try {
                    await API.delete(RESERVATION_INVOICE_DELETE_BILL_ENDPOINT(this.metaReservationId), {
                        data: { id: this.paymentId },
                    });
                    await this.refreshReservationInvoice(this.metaReservationId);
                } catch (e) {
                    this.errorToast({ text: `Could not delete payment.` });
                } finally {
                    this.busy = false;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    :deep(.ag-header-cell-text) {
        text-align: right !important;
    }
    :deep(.automated-payment-prediction) {
        background: linear-gradient(90deg, rgba(146, 254, 157, 0.06) 0%, rgba(0, 201, 255, 0.06) 100%) !important;
    }
    :deep(.automated-payment-notification) {
        background: linear-gradient(90deg, rgba(255, 78, 80, 0.1) 0%, rgba(249, 212, 35, 0.1) 100%) !important;
    }
</style>
